import { useEffect, type ReactNode } from "react";
import { XIcon } from "lucide-react";
import { Button } from "@baluu/ui/components/button";
import { cn } from "@baluu/ui/lib/utils";
import { closesOnEscape } from "~/widgets/lib/purchase-overlay";

export type PurchaseOverlayShellProps = {
  title: string;
  /** A line under the title. */
  intro?: string;
  onClose: () => void;
  /** Wide enough for cards side by side. */
  wide?: boolean;
  /** A dialog over the overlay (sign-in) is open: Escape is its, not ours. */
  nestedOpen?: boolean;
  children: ReactNode;
};

/**
 * The frame the pass and membership overlays share: a dimmed full-screen
 * backdrop (the checkout's `.checkout-overlay-content`), a panel in the
 * school's colours with its title and a close button. Escape and a click on
 * the backdrop close it, as the checkout's do.
 */
export function PurchaseOverlayShell({ title, intro, onClose, wide, nestedOpen = false, children }: PurchaseOverlayShellProps) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (closesOnEscape(event, nestedOpen)) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, nestedOpen]);

  return (
    <div
      className="checkout-overlay-content flex items-start justify-center overflow-y-auto bg-black/50"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cn(
          "relative mx-4 my-8 w-full overflow-hidden rounded-xl bg-background text-foreground shadow-xl",
          wide ? "max-w-5xl" : "max-w-lg",
        )}
      >
        <div className="flex items-start justify-between gap-4 border-b border-border px-6 py-4">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold">{title}</h2>
            {intro ? <p className="mt-1 text-sm text-muted-foreground">{intro}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-2 rounded-md p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <XIcon className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

/** A message in place of the offers (loading failed, nothing on sale). */
export function PurchaseOverlayNotice({ children }: { children: ReactNode }) {
  return <p className="py-6 text-center text-sm text-muted-foreground">{children}</p>;
}

/** What couldn't load, with a way to ask again. */
export function PurchaseOverlayRetry({ children, onRetry }: { children: ReactNode; onRetry: () => void }) {
  return (
    <div role="alert" className="flex flex-col items-center gap-3 py-6 text-center">
      <p className="text-sm text-muted-foreground">{children}</p>
      <Button variant="outline" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}

/** Placeholder cards while the offers load. */
export function PurchaseOverlaySkeleton() {
  return (
    <div className="grid gap-4" aria-busy="true">
      {[0, 1].map((i) => (
        <div key={i} className="h-32 animate-pulse rounded-lg bg-muted" />
      ))}
    </div>
  );
}
